import type { Materia, Unidad, Voto } from '@condo/contracts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UnidadVoteBlock } from './unidad-vote-block';

const CATEGORIA_LABELS: Record<Materia['categoria'], string> = {
  informativa: 'Informativa',
  gestion_simple: 'Gestion simple',
  aprobacion_gasto: 'Aprobacion de gasto',
  obra_mayor: 'Obra mayor',
  modificacion_reglamento: 'Modificacion de reglamento',
  cambio_destino: 'Cambio de destino',
  eleccion_cargos: 'Eleccion de cargos',
};

const TIPO_VOTO_LABELS: Record<Materia['tipoVoto'], string> = {
  si_no: 'Si / No',
  opciones_multiples: 'Opciones multiples',
  eleccion_cargos: 'Eleccion de cargos',
};

interface Props {
  condoId: string;
  asambleaId: string;
  materia: Materia;
  /** Unidades propias del propietario (filtradas externamente) */
  unidades: Unidad[];
  /** Todos los votos propios de esta asamblea */
  misVotos: Voto[];
}

/**
 * Tarjeta de votacion para una materia.
 * Renderiza un UnidadVoteBlock por cada unidad propia.
 * Las materias informativas se muestran solo en modo lectura.
 */
export function MateriaVotoCard({
  condoId,
  asambleaId,
  materia,
  unidades,
  misVotos,
}: Props) {
  const esInformativa = materia.categoria === 'informativa';
  const votosDeEstaMateria = misVotos.filter((v) => v.materiaId === materia.id);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-[0.6rem] mb-1">
              Materia {String(materia.orden + 1).padStart(2, '0')}
            </p>
            <CardTitle className="text-base leading-snug">{materia.titulo}</CardTitle>
            {materia.descripcion && (
              <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
                {materia.descripcion}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1 items-end shrink-0">
            <Badge variant="neutral" className="text-[0.625rem]">
              {CATEGORIA_LABELS[materia.categoria]}
            </Badge>
            {!esInformativa && (
              <Badge variant="primary" className="text-[0.625rem]">
                {TIPO_VOTO_LABELS[materia.tipoVoto]}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {esInformativa ? (
          <p className="text-sm text-[var(--color-ink-muted)] italic">
            Esta materia es solo informativa. No requiere votacion.
          </p>
        ) : (
          <div className="space-y-3">
            {unidades.map((unidad) => {
              const votoExistente = votosDeEstaMateria.find(
                (v) => v.unidadId === unidad.id,
              );
              return (
                <UnidadVoteBlock
                  key={unidad.id}
                  condoId={condoId}
                  asambleaId={asambleaId}
                  materia={materia}
                  unidad={unidad}
                  votoExistente={votoExistente}
                />
              );
            })}
            {unidades.length === 0 && (
              <p className="text-sm text-[var(--color-ink-muted)]">
                No tienes unidades asignadas para votar en esta materia.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
